/**
 * Email Templates Admin Page
 * Edit and preview email templates stored in the database
 */

import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Eye, RefreshCw, Save, Trash2 } from 'lucide-react';
import { Button, Input, Label } from '../../components/ui';
import { ConfirmDialog } from '../../components/ui';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Modal } from '../../components/ui/modal';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { pageStyles, cardStyles } from '../../lib/styles';

interface EmailTemplate {
  id: string;
  slug?: string;
  name: string;
  subject: string;
  bodyHtml: string;
  bodyText: string | null;
  triggerEvent: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface PreviewData {
  subject: string;
  bodyHtml: string;
  sampleData: Record<string, string>;
}

interface DraftTemplate {
  name: string;
  subject: string;
  bodyHtml: string;
  bodyText: string;
  isActive: boolean;
}

export function EmailTemplatesPage() {
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<EmailTemplate | null>(null);
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [previewMode, setPreviewMode] = useState<'rendered' | 'html'>('rendered');
  const [editorMode, setEditorMode] = useState<'visual' | 'html'>('visual');
  const [isSaving, setIsSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<EmailTemplate | null>(null);

  const { data: dbTemplates = [], isLoading, refetch } = useQuery({
    queryKey: ['admin', 'templates'],
    queryFn: () => api.get<EmailTemplate[]>('/api/admin/templates?include_inactive=true'),
  });

  useEffect(() => {
    if (!selectedTemplateId && dbTemplates.length > 0) {
      setSelectedTemplateId(dbTemplates[0].id);
    }
  }, [dbTemplates, selectedTemplateId]);

  const selectedTemplate = useMemo(
    () => dbTemplates.find((template) => template.id === selectedTemplateId) || null,
    [dbTemplates, selectedTemplateId]
  );

  const [draft, setDraft] = useState<DraftTemplate>({
    name: '',
    subject: '',
    bodyHtml: '',
    bodyText: '',
    isActive: true,
  });

  useEffect(() => {
    if (!selectedTemplate) return;
    setDraft({
      name: selectedTemplate.name,
      subject: selectedTemplate.subject,
      bodyHtml: selectedTemplate.bodyHtml,
      bodyText: selectedTemplate.bodyText || '',
      isActive: selectedTemplate.isActive,
    });
  }, [selectedTemplate]);

  const isDirty = Boolean(
    selectedTemplate && (
      draft.name !== selectedTemplate.name ||
      draft.subject !== selectedTemplate.subject ||
      draft.bodyHtml !== selectedTemplate.bodyHtml ||
      draft.bodyText !== (selectedTemplate.bodyText || '') ||
      draft.isActive !== selectedTemplate.isActive
    )
  );

  const handlePreview = async (template: EmailTemplate) => {
    setPreviewTemplate(template);
    setPreviewData(null);
    setIsPreviewLoading(true);
    setPreviewMode('rendered');

    try {
      const response = await api.post<PreviewData>(
        `/api/admin/templates/${template.id}/preview`,
        {}
      );
      setPreviewData(response);
    } catch (error) {
      console.error('Error previewing template:', error);
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedTemplate) return;

    setIsSaving(true);
    try {
      await api.patch<EmailTemplate>(`/api/admin/templates/${selectedTemplate.id}`, {
        name: draft.name.trim() || selectedTemplate.name,
        subject: draft.subject.trim() || selectedTemplate.subject,
        bodyHtml: draft.bodyHtml || selectedTemplate.bodyHtml,
        bodyText: draft.bodyText.trim() ? draft.bodyText : null,
        isActive: draft.isActive,
      });
      await refetch();
    } catch (error) {
      console.error('Error saving template:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const closePreview = () => {
    setPreviewTemplate(null);
    setPreviewData(null);
  };

  const handleDeleteTemplate = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/api/admin/templates/${deleteTarget.id}`);
      if (selectedTemplateId === deleteTarget.id) {
        setSelectedTemplateId(null);
      }
      setDeleteTarget(null);
      await refetch();
    } catch (error) {
      console.error('Error deleting template:', error);
      setDeleteTarget(null);
    }
  };

  return (
    <div className={pageStyles.container}>
      <div className={pageStyles.header}>
        <div>
          <h1 className={pageStyles.title}>Plantillas de Email</h1>
          <p className={pageStyles.subtitle}>Gestiona plantillas basadas en la base de datos</p>
        </div>
        <Button variant="secondary" size="sm" onClick={() => refetch()}>
          <RefreshCw className="h-3 w-3 mr-1" />
          Actualizar
        </Button>
      </div>

      <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
        <div className={cardStyles.container}>
          <div className={cardStyles.header}>
            <span className="text-xs font-medium text-gray-900">Plantillas</span>
            <span className="text-[10px] text-gray-500">{dbTemplates.length} total</span>
          </div>
          <div className={cardStyles.content}>
            {isLoading ? (
              <div className="text-xs text-gray-500">Cargando plantillas...</div>
            ) : (
              <div className="space-y-2">
                {dbTemplates.map((template) => {
                  const isSelected = template.id === selectedTemplateId;
                  return (
                    <button
                      key={template.id}
                      className={`w-full text-left px-3 py-2 rounded border transition ${
                        isSelected
                          ? 'border-purple-300 bg-purple-50'
                          : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'
                      }`}
                      onClick={() => setSelectedTemplateId(template.id)}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-xs font-medium text-gray-900 truncate">
                            {template.name}
                          </div>
                          <div className="text-[10px] text-gray-500 truncate">
                            {template.triggerEvent}
                          </div>
                          <div className="text-[10px] text-gray-400 truncate">{template.id}</div>
                        </div>
                        <div className="flex items-center gap-1">
                          <span
                            className={`text-[10px] px-1.5 py-0.5 rounded border ${
                              template.isActive
                                ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                                : 'bg-gray-100 border-gray-200 text-gray-500'
                            }`}
                          >
                            {template.isActive ? 'Activa' : 'Inactiva'}
                          </span>
                          <button
                            type="button"
                            className="p-0.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-500"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteTarget(template);
                            }}
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className={cardStyles.container}>
          <div className={cardStyles.header}>
            <div>
              <span className="text-xs font-medium text-gray-900">Editor</span>
              {selectedTemplate && (
                <div className="text-[10px] text-gray-500">{selectedTemplate.triggerEvent}</div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => selectedTemplate && handlePreview(selectedTemplate)}
                disabled={!selectedTemplate}
              >
                <Eye className="h-3 w-3 mr-1" />
                Vista previa
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={!selectedTemplate || !isDirty || isSaving}
              >
                <Save className="h-3 w-3 mr-1" />
                Guardar
              </Button>
            </div>
          </div>
          <div className={cardStyles.content}>
            {!selectedTemplate ? (
              <div className="text-xs text-gray-500">Selecciona una plantilla para editar.</div>
            ) : (
              <div className="space-y-4">
                <div className="grid gap-4 md:grid-cols-[1fr_auto]">
                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase tracking-wide text-gray-500">Nombre</Label>
                    <Input
                      value={draft.name}
                      onChange={(event) => setDraft((prev) => ({ ...prev, name: event.target.value }))}
                    />
                  </div>
                  <div className="flex items-center gap-2 pt-5">
                    <Switch
                      checked={draft.isActive}
                      onCheckedChange={(value) => setDraft((prev) => ({ ...prev, isActive: value }))}
                    />
                    <span className="text-xs text-gray-600">Activa</span>
                  </div>
                </div>

                <div className="space-y-1">
                  <Label className="text-[10px] uppercase tracking-wide text-gray-500">Asunto</Label>
                  <Input
                    value={draft.subject}
                    onChange={(event) => setDraft((prev) => ({ ...prev, subject: event.target.value }))}
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-[10px] uppercase tracking-wide text-gray-500">Contenido HTML</Label>
                    <div className="flex items-center gap-1 text-[10px] text-gray-500">
                      <button
                        className={`px-2 py-0.5 rounded ${editorMode === 'visual' ? 'bg-purple-50 text-purple-700' : 'hover:bg-gray-100'}`}
                        onClick={() => setEditorMode('visual')}
                      >
                        Visual
                      </button>
                      <button
                        className={`px-2 py-0.5 rounded ${editorMode === 'html' ? 'bg-purple-50 text-purple-700' : 'hover:bg-gray-100'}`}
                        onClick={() => setEditorMode('html')}
                      >
                        HTML
                      </button>
                    </div>
                  </div>

                  {editorMode === 'visual' ? (
                    <RichHtmlEditor
                      value={draft.bodyHtml}
                      onChange={(value) => setDraft((prev) => ({ ...prev, bodyHtml: value }))}
                    />
                  ) : (
                    <Textarea
                      rows={12}
                      value={draft.bodyHtml}
                      onChange={(event) => setDraft((prev) => ({ ...prev, bodyHtml: event.target.value }))}
                      className="font-mono text-xs"
                    />
                  )}
                </div>

                <div className="space-y-1">
                  <Label className="text-[10px] uppercase tracking-wide text-gray-500">Texto Plano (opcional)</Label>
                  <Textarea
                    rows={4}
                    value={draft.bodyText}
                    onChange={(event) => setDraft((prev) => ({ ...prev, bodyText: event.target.value }))}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal
        isOpen={Boolean(previewTemplate)}
        onClose={closePreview}
        title={previewTemplate?.name || 'Vista previa'}
      >
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-xs text-gray-500">{previewTemplate?.triggerEvent}</div>
            <div className="inline-flex items-center rounded bg-gray-100 p-1 text-[10px]">
              <button
                onClick={() => setPreviewMode('rendered')}
                className={`px-3 py-1 text-xs rounded ${
                  previewMode === 'rendered'
                    ? 'bg-white shadow text-gray-900'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Renderizado
              </button>
              <button
                onClick={() => setPreviewMode('html')}
                className={`px-3 py-1 text-xs rounded ${
                  previewMode === 'html'
                    ? 'bg-white shadow text-gray-900'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                HTML
              </button>
            </div>
          </div>

          {previewData && (
            <div className="px-3 py-2 bg-gray-50 rounded border border-gray-200">
              <span className="text-[10px] text-gray-500">Asunto:</span>
              <p className="text-sm font-medium text-gray-900">{previewData.subject}</p>
            </div>
          )}

          <div className="border border-gray-200 rounded-lg overflow-hidden">
            {isPreviewLoading ? (
              <div className="h-64 flex items-center justify-center">
                <RefreshCw className="h-5 w-5 animate-spin text-gray-400" />
              </div>
            ) : previewMode === 'rendered' ? (
              <iframe
                srcDoc={previewData?.bodyHtml || previewTemplate?.bodyHtml || ''}
                className="w-full h-[400px] bg-white"
                title="Email Preview"
              />
            ) : (
              <pre className="p-3 text-[10px] leading-relaxed overflow-auto max-h-[400px] bg-gray-900 text-gray-100">
                <code>{previewData?.bodyHtml || previewTemplate?.bodyHtml || ''}</code>
              </pre>
            )}
          </div>

          {previewData?.sampleData && (
            <div className="text-[10px] text-gray-500">
              <span className="font-medium">Variables de ejemplo:</span>
              <div className="mt-1 flex flex-wrap gap-1">
                {Object.entries(previewData.sampleData).map(([key, value]) => (
                  <span key={key} className="px-1.5 py-0.5 bg-gray-100 rounded">
                    {`{{${key}}}`}: {value}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end pt-2">
            <Button variant="secondary" size="sm" onClick={closePreview}>
              Cerrar
            </Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        title="Eliminar plantilla"
        description={`¿Eliminar "${deleteTarget?.name}"? Esta acción es irreversible.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        variant="destructive"
        onConfirm={handleDeleteTemplate}
      />
    </div>
  );
}

interface RichHtmlEditorProps {
  value: string;
  onChange: (value: string) => void;
}

function RichHtmlEditor({ value, onChange }: RichHtmlEditorProps) {
  const editorRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!editorRef.current) return;
    if (editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value;
    }
  }, [value]);

  const execCommand = (command: string, commandValue?: string) => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    document.execCommand(command, false, commandValue);
  };

  const handleCreateLink = () => {
    const url = window.prompt('URL del enlace');
    if (url) {
      execCommand('createLink', url);
    }
  };

  const handleInput = (event: FormEvent<HTMLDivElement>) => {
    onChange(event.currentTarget.innerHTML);
  };

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="flex flex-wrap gap-2 p-2 bg-gray-50 border-b border-gray-200 text-xs">
        <button
          type="button"
          className="px-2 py-1 rounded hover:bg-white"
          onClick={() => execCommand('bold')}
        >
          Negrita
        </button>
        <button
          type="button"
          className="px-2 py-1 rounded hover:bg-white"
          onClick={() => execCommand('italic')}
        >
          Cursiva
        </button>
        <button
          type="button"
          className="px-2 py-1 rounded hover:bg-white"
          onClick={() => execCommand('underline')}
        >
          Subrayado
        </button>
        <button
          type="button"
          className="px-2 py-1 rounded hover:bg-white"
          onClick={() => execCommand('insertUnorderedList')}
        >
          Lista
        </button>
        <button
          type="button"
          className="px-2 py-1 rounded hover:bg-white"
          onClick={() => execCommand('insertOrderedList')}
        >
          Lista ordenada
        </button>
        <button
          type="button"
          className="px-2 py-1 rounded hover:bg-white"
          onClick={handleCreateLink}
        >
          Enlace
        </button>
        <button
          type="button"
          className="px-2 py-1 rounded hover:bg-white"
          onClick={() => execCommand('removeFormat')}
        >
          Limpiar
        </button>
      </div>
      <div
        ref={editorRef}
        className="min-h-[240px] p-3 text-sm focus:outline-none"
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
      />
    </div>
  );
}
